import React from 'react'
import PropTypes from 'prop-types'
import {Button, Collapse, Dropdown, Space, Menu, message} from 'antd'
import {
  CarOutlined,
  EditOutlined,
  UserOutlined,
  PhoneOutlined,
  DollarOutlined,
  WarningOutlined,
  PictureOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  OrderedListOutlined,
  ShoppingCartOutlined,
  DownOutlined,
} from '@ant-design/icons'
import get from 'lodash.get'
import moment from 'moment'
import CopyToClipboard from 'copy-to-clipboard'
import {BookingStateIcon, Link} from 'components/shared'
import {googleMapsLink, guleSiderMapLink, maps1881Link} from 'lib/util'
import getBookingReminders from 'lib/getBookingReminders'

import StylistManus from 'assets/icons/stylist-manus.svg'
import DronePhotoIcon from 'assets/icons/product-drone-photo.svg'
import ShowAssetsIcon from 'assets/icons/show-assets.svg'
import ExpressDelivery from 'assets/icons/express-delivery.svg'
import Key from 'assets/icons/key.svg'

import Upsell from './Upsell'
import AreaPhotos from './AreaPhotos'
import EstatePerson from './EstatePerson'
import OtherBookings from './OtherBookings'
import BookingCollapse from './BookingCollapse'
import PreviousUpsellOrders from './PreviousUpsellOrders'
import ManageProducts from './ManageProducts'
import Deviation from './Deviation'
import InvoiceLines from './InvoiceLines'

import './index.css'

const BookingOverview = ({booking, viewerId}) => {
  // This component is absolutely not finished. Most of the content
  // is just moved/copied from other places and placed into Antd Collapse

  const startsAt = moment(booking.startsAt)
  const endsAt = moment(booking.endsAt)

  const estateSellerPhone = get(booking, 'estate.estateSellers', [])
    .filter(seller => seller.type === 'EstateSeller')
    .map(seller => seller.phone)
    .filter(phone => phone)[0]

  const sourceNames = {
    attendance: 'Fra bestilling',
    contactPerson: 'Fra kontaktperson',
    customer: 'Fra meglerkontor',
    chain: 'Fra region',
    region: 'Fra kjede',
  }

  const panelHeader = (title, icon) => {
    return (<div className="custom-header-icon">{icon}{title}</div>)
  }

  const reminders = getBookingReminders(booking)

  const hasTextFromStylist = booking.textsFromStylists.length > 0
  const hasDrone = booking.products.edges.some(product => product.node.__typename === 'DronePhotoProduct' || product.node.drone)
  const linkToGoogleMaps = booking && booking.estate
    ? googleMapsLink(booking.estate.location)
    : null
  const linkToGuleSiderMap = booking && booking.estate
    ? guleSiderMapLink(booking.estate)
    : null
  const linkTo1881Map = booking && booking.estate
    ? maps1881Link(booking.estate)
    : null

  const keyPickupReminder = reminders.find(r => r.type === 'key-pickup')

  // StylingProduct should really be delivered through the /delivery path as well,
  // but that is currently not possible as it requires both a document asset
  // and a supplemental text asset delivered. This is a compromise so photographers
  // can start testing the uploader for delivering photos and urls.
  const stylingProduct = booking.products.edges.find(e => e.node.__typename === 'StylingProduct')
  const deliveryPath = stylingProduct
    ? `/bookings/${booking.id}/delivery/${stylingProduct.node.id}`
    : `/bookings/${booking.id}/delivery`

  const videoProducts = booking.products.edges.map(e => e.node).filter(node => node.__typename === 'VideoProduct')
  const digitalPlanDrawingProducts = booking.products.edges.map(e => e.node).filter(node => node.__typename === 'DigitalPlanDrawingProduct')
  const planDrawingRefs = digitalPlanDrawingProducts.map(p => ({ id: p.id, weScanLink: p.weScanLink, weScanRef: p.productionReference })).filter(p => p.weScanLink || p.weScanRef)

  const estateLocationInfo = () => {
    const keyMap = {floor: 'Etasje', doorbell: 'Navn på ringeklokke', additionalInfo: 'Annen info'}
    const fields = []
    Object.entries(keyMap).forEach(([key, label]) => {
      const value = booking.estate[key]
      if (value) {
        fields.push({label, value})
      }
    })

    if (fields.length) {
      return (
        <div className="estate-location-info">
          <div className="estate-info-header">Boliginformasjon</div>
          {fields.map(f => (
            <div key={f.label}>{f.label}: {f.value}</div>
          ))}
        </div>
      )
    }
  }

  const handleSmsClick = (e) => {
    // Check if we're inside an iOS web app.
    // The href "sms:" is for some reason not working there.
    if (window.navigator.standalone === true) {
      e.preventDefault()
      window.open(e.currentTarget.href, '_self')
    }
  }

  const copyRefToClipboard = (text) => {
    if (CopyToClipboard(text)) {
      message.info('Plantegning-ref kopiert til utklippstavlen')
    }
  }

  return (
    <div className="booking">
      <div className="section top-details">
        <div className="inviso-ref">
          {(booking.expressDelivery || booking.pickUpKeysInstructions) && (
            <span className="title-icons">
              {booking.expressDelivery && <img src={ExpressDelivery} alt="Hastelevering" width="20" height="20" />}
              {booking.pickUpKeysInstructions && <img src={Key} alt="Nøkkel" width="20" height="20" />}
            </span>
          )}
          EFKT ref. #{booking.estate.invisoRef} ({booking.estate.segment.name})
        </div>
        <div className="state">
          <BookingStateIcon state={booking.state} />
        </div>
      </div>

      <div className="section contact-details">
        <span>
          <a href={`tel:${booking.estate.broker.phone}`}>
            <Button icon={<PhoneOutlined />}>Ring megler</Button>
          </a>
          <a href={`sms:${booking.estate.broker.phone}`} onClick={handleSmsClick}>
            <Button icon={<PhoneOutlined />}>SMS megler</Button>
          </a>
        </span>
        <span>
          <a href={`tel:${estateSellerPhone}`}>
            <Button icon={<PhoneOutlined />}>Ring selger</Button>
          </a>
          <a href={`sms:${estateSellerPhone}`} onClick={handleSmsClick}>
            <Button icon={<PhoneOutlined />}>SMS selger</Button>
          </a>
        </span>
      </div>

      <div className="section datetime-details">
        <div className="date with-icon">
          <CalendarOutlined />
          {startsAt.format('Do MMM')}
        </div>
        <div className="time with-icon">
          <ClockCircleOutlined />
          {startsAt.format('[kl.]HH:mm')} - {endsAt.format('[kl.]HH:mm')}
        </div>
      </div>

      {reminders.filter(r => r.type !== 'key-pickup').map(n => (
        <div key={n.type} className="section with-icon">
          <img src={n.icon} alt={n.text} width="20" height="20" className="key-icon" />
          {n.text}
        </div>
      ))}

      <div className="section products-details with-icon">
        <div>
          <InfoCircleOutlined />
          {booking.products.edges.map(({node: product}, i) =>
            <span key={product.id} className={product.state}>
              {product.quantity ? `${product.quantity} ` : null}
              {product.name}
              {i < booking.products.edges.length - 1 ? ', ' : null}
            </span>
          )}
        </div>
      </div>

      {hasDrone && (
        <div className="section">
          <img src={DronePhotoIcon} alt="Drone" width="25" className="drone-icon" />
          <a
            href="https://www.safetofly.no/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Safetofly.no
          </a>
        </div>
      )}

      <div className="section with-icon">
        {!!linkToGoogleMaps && (
          <div className="map-link">
            <CarOutlined />
            <a href={linkToGoogleMaps} target="_blank" rel="noopener noreferrer">Link til kart (Google)</a>
          </div>
        )}

        {!!linkToGuleSiderMap && (
          <div className="map-link">
            <CarOutlined />
            <a href={linkToGuleSiderMap} target="_blank" rel="noopener noreferrer">Link til kart (Gule sider)</a>
          </div>
        )}

        {!!linkTo1881Map && (
          <div className="map-link">
            <CarOutlined />
            <a href={linkTo1881Map} target="_blank" rel="noopener noreferrer">Link til kart (1881)</a>
          </div>
        )}
      </div>

      <div className="section">
        <BookingCollapse accordion bordered={false}>
          <Collapse.Panel
            header={panelHeader('Informasjon', <InfoCircleOutlined />)}
            className="info-section"
          >
            {!!keyPickupReminder && (
              <div>
                <img src={keyPickupReminder.icon} alt={keyPickupReminder.text} width="20" height="20" className="key-icon" />
                Nøkkelhenting: {booking.pickUpKeysInstructions}
              </div>
            )}
            <div>
              <h3>Adresse</h3>
              <div>{booking.estate.streetAddress}</div>
              <div>{booking.estate.postalCode} {booking.estate.postalArea}</div>
            </div>
            {estateLocationInfo()}
          </Collapse.Panel>

          <Collapse.Panel header={panelHeader('Se leveranse', <img src={ShowAssetsIcon} alt="" />) }>
            <Link to={`/bookings/${booking.id}/assets`}>
              Se leveranse
            </Link>
          </Collapse.Panel>

          {hasTextFromStylist && (
            <Collapse.Panel header={panelHeader('Manus fra boligregi', <img src={StylistManus} alt="" />)}>
              {booking.textsFromStylists.map(({text, stylist}, i) =>(
                <div className="text-from-stylist" key={i}>
                  <div className="stylist-info">
                    <div className="name with-icon">
                      <UserOutlined />
                      {stylist.name}
                    </div>
                    {!!stylist.phone && (
                      <div className="phone with-icon">
                        <PhoneOutlined />
                        <a href={`tel:${stylist.phone}`}>
                          {stylist.phone}
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="text">
                    {text}
                  </div>
                </div>
              ))}
            </Collapse.Panel>
          )}

          <Collapse.Panel
            header={panelHeader('Instruks til frilanser', <OrderedListOutlined />)}
            disabled={!booking.instructionsForPhotographer.length && videoProducts.length === 0 && !planDrawingRefs.length}
          >
            {booking.instructionsForPhotographer.map(({source, instructions}) =>
              <div key={source}>
                <h3>{sourceNames[source]}</h3>
                <p>{instructions}</p>
              </div>
            )}

            {videoProducts.length > 0 && (
              <div>
                <h3>Instruksjoner for video</h3>
                {videoProducts.map(video => (
                  <dl key={video.id}>
                    <dt>Bestilt video</dt>
                    <dd>{video.videoVariant}</dd>
                    <dt>Bestilt dronefilm</dt>
                    <dd>{video.drone ? 'Ja' : 'Nei'}</dd>
                    {video.features.map(({name, value}) => (
                      <React.Fragment key={name}>
                        <dt>{name}</dt>
                        <dd>{value}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                ))}
              </div>
            )}

            {planDrawingRefs.length > 0 && (
              <div>
                <dl>
                  {planDrawingRefs.map((ref, i) => (
                    <React.Fragment key={i}>
                      {!!ref.weScanLink && (
                        <>
                          <dt>WeScan</dt>
                          <dd><a href={ref.weScanLink}>{ref.weScanLink}</a></dd>
                        </>
                      )}
                      <dt>WeScan-ref</dt>
                      <dd>{ref.weScanRef}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            )}
          </Collapse.Panel>

          <Collapse.Panel header={panelHeader('Meglers informasjon', <UserOutlined />)}>
            <EstatePerson person={booking.estate.broker} />
          </Collapse.Panel>

          <Collapse.Panel header={panelHeader('Selgers informasjon', <UserOutlined />)}>
            <EstatePerson person={booking.estate.estateSellers[0]} />
          </Collapse.Panel>

          <Collapse.Panel
            header={panelHeader('Alternativ kontaktperson', <UserOutlined />)}
            disabled={booking.estate.estateSellers.length < 2}
          >
            {booking.estate.estateSellers[1] ? <EstatePerson person={booking.estate.estateSellers[1]} /> : null}

          </Collapse.Panel>

          <Collapse.Panel header={panelHeader('Se bilder i nærområdet', <PictureOutlined />)}>
            <AreaPhotos booking={booking} />
          </Collapse.Panel>

          <Collapse.Panel header={panelHeader('Endre bestilte produkter', <EditOutlined />)}>
            <ManageProducts booking={booking} />
          </Collapse.Panel>

          <Collapse.Panel
            header={panelHeader('Mersalg', <ShoppingCartOutlined />)}
            disabled={booking.availableProducts.length < 1}
          >
            <Upsell booking={booking} />
          </Collapse.Panel>

          <Collapse.Panel header={panelHeader('Bookinger', <CalendarOutlined />)}>
            <OtherBookings booking={booking} serviceProviderId={viewerId} />
          </Collapse.Panel>

          <Collapse.Panel
            header={panelHeader('Tidligere mersalgsbestillinger', <ShoppingCartOutlined />)}
            disabled={booking.orderInvoiceLines.length < 1}
          >
            <PreviousUpsellOrders booking={booking} />
          </Collapse.Panel>

          <Collapse.Panel header={panelHeader('Avvik ved booking', <WarningOutlined />)}>
            <Deviation booking={booking} />
          </Collapse.Panel>

          <Collapse.Panel header={panelHeader('Honorarer', <DollarOutlined />)}>
            <InvoiceLines bookingId={booking.id} />
          </Collapse.Panel>
        </BookingCollapse>
      </div>

      <div className="section bottom-actions">
        <Link to={deliveryPath}>
          <Button>Leveranse</Button>
        </Link>

        {planDrawingRefs.length > 1 && (
          <Dropdown overlay={
            <Menu>
              {planDrawingRefs.map((ref, i) => (
                <Menu.Item key={i} onClick={ref.weScanLink ? undefined : () => copyRefToClipboard(ref.weScanRef)}>
                  {ref.weScanLink
                    ? <a href={ref.weScanLink}>
                        <Button>{ref.weScanLink}</Button>
                      </a>
                    : ref.weScanRef
                  }
                </Menu.Item>
              ))}
            </Menu>
          }>
            <Button>
              <Space>
                WeScan
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown>
        )}

        {planDrawingRefs.length === 1 && (
          <>
            {planDrawingRefs[0].weScanLink
              ? <a href={planDrawingRefs[0].weScanLink}>
                  <Button>Åpne WeScan-app</Button>
                </a>
              : <Button onClick={() => copyRefToClipboard(planDrawingRefs[0].weScanRef)}>
                  Kopier WeScan-ref
                </Button>
            }
          </>
        )}
      </div>
    </div>
  )
}

BookingOverview.propTypes = {
  booking: PropTypes.object.isRequired,
  viewerId: PropTypes.string.isRequired,
}

export default BookingOverview
